import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import '../job-roles.css';

const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

export interface ModalDialogProps {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** While true (a request is in flight) Escape and the close button do nothing, so a save is never abandoned half-way. */
  busy?: boolean;
  role?: 'dialog' | 'alertdialog';
  /** Extra text (a short explanation) the dialog is described by. */
  describedBy?: string;
  closeLabel?: string;
  /** Extra class on the dialog element (a screen-specific skin such as the Contractor Payments blue-header modal). */
  className?: string;
}

/**
 * The Job Role Library modal: `role="dialog"` + `aria-modal`, labelled by its title, focus moves in on open (to the element marked
 * `data-autofocus`, otherwise the dialog itself so a screen reader announces its name), Tab and Shift+Tab wrap inside it, Escape
 * closes it, and focus returns to whatever opened it. It renders in a portal, and it stops every keydown from reaching React
 * ancestors, so a dialog opened from inside another dialog (Create new role from Add Employee) never closes or traps its parent.
 */
export function ModalDialog({ title, onClose, children, footer, size = 'md', busy = false, role = 'dialog', describedBy, closeLabel, className }: ModalDialogProps) {
  const uid = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<Element | null>(typeof document === 'undefined' ? null : document.activeElement);
  const bodyRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scrolls, setScrolls] = useState(false);

  // A scrolling region must be reachable from the keyboard, so the body takes a tab stop only while its content overflows.
  useEffect(() => {
    const body = bodyRef.current;
    const inner = innerRef.current;
    if (!body || !inner || typeof ResizeObserver === 'undefined') return;
    const measure = () => setScrolls(body.scrollHeight > body.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(body);
    observer.observe(inner);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const opener = openerRef.current;
    const dialog = dialogRef.current;
    (dialog?.querySelector<HTMLElement>('[data-autofocus]') ?? dialog)?.focus();
    return () => {
      if (opener instanceof HTMLElement && document.contains(opener)) opener.focus();
    };
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    // Never let a key pressed in this dialog reach a parent dialog's handlers (React events bubble through portals).
    e.stopPropagation();
    if (e.key === 'Escape') {
      if (!busy) onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const nodes = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
    const first = nodes[0];
    const last = nodes[nodes.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  const titleId = `${uid}-title`;
  return createPortal(
    <div className="jr-backdrop" role="presentation">
      <div
        ref={dialogRef}
        className={`jr-modal jr-modal--${size}${className ? ` ${className}` : ''}`}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        {...(describedBy ? { 'aria-describedby': describedBy } : {})}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="jr-modal__head">
          <h3 id={titleId}>{title}</h3>
          <button type="button" className="jr-modal__close" aria-label={closeLabel ?? `Close ${title}`} onClick={onClose} disabled={busy}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
        <div
          ref={bodyRef}
          className="jr-modal__body"
          {...(scrolls ? { tabIndex: 0, role: 'region', 'aria-label': `${title} content` } : {})}
        >
          <div ref={innerRef} className="jr-modal__inner">{children}</div>
        </div>
        {footer && <div className="jr-modal__foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
